/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../../../app/nls/StringResources',
	'../../../dashboard/layout/LayoutHelper',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../lib/@waca/baglass/js/baglass/utils/Utils',
	'../../../lib/@waca/dashboard-common/dist/ui/dialogs/ConfirmationDialog',
	'../../../lib/@waca/dashboard-common/dist/api/PropertiesProviderAPI',
	'underscore',
	'../../../dashboard/widgets/PropertyListUtil'
], (StringResources, LayoutHelper, APIFactory, Utils, ConfirmationDialog, PropertiesProviderAPI, _, PropertyListUtil) => {
	const MINIMUM_PAGESIZE = { width: 320, height: 200 };

	const PAGESIZES = {
		'16:9': {
			'width': 1280,
			'height': 720
		},
		'4:3': {
			'width': 1280,
			'height': 960
		},
		'letter': {
			'width': 612,
			'height': 792
		},
		'legal': {
			'width': 612,
			'height': 1008
		},
		'a4': {
			'width': 595,
			'height': 842
		},
		'tabloid': {
			'width': 792,
			'height': 1224
		},
		'infographic': {
			'width': 512,
			'height': 1024
		}
	};
	const PRESET_PAGE_SIZES = [{
		label: StringResources.get('propPageSizePresetCustom'),
		value: 'custom'
	}, {
		label: StringResources.get('propPageSizePreset16x9'),
		value: '16:9'
	}, {
		label: StringResources.get('propPageSizePreset4x3'),
		value: '4:3'
	}, {
		label: StringResources.get('letter'),
		value: 'letter'
	}, {
		label: StringResources.get('legal'),
		value: 'legal'
	}, {
		label: StringResources.get('a4'),
		value: 'a4'
	}, {
		label: StringResources.get('tabloid'),
		value: 'tabloid'
	}, {
		label: StringResources.get('propPageSizePresetInfographic'),
		value: 'infographic'
	}];

	class TopLevelProperties {
		constructor(options) {
			this._features = options.features;
			this.dashboard = options.dashboardApi || options.dashboardAPI;

			const featureChecker = this.dashboard.getGlassCoreSvc('.FeatureChecker');
			this.isPropertiesV2Enabled = featureChecker && featureChecker.checkValue('dashboard', 'PropertiesV2', 'enabled');

			this.content = options.content;
			options.features['Properties'].registerProvider(this.getAPI());
		}

		getCanvas() {
			if (!this.canvas) {
				this.canvas = this.dashboard.getCanvas();
			}
			return this.canvas;
		}

		getLayoutModel() {
			const boardModel = this._features['internal'].getBoardModel();
			return boardModel.layout.findModel(this.content.getId());
		}

		getAPI() {
			return APIFactory.createAPI(this, [PropertiesProviderAPI]);
		}

		_getCommonType(contentType) {
			return contentType && contentType.split('.')[0];
		}

		_getSubType(contentType) {
			if (contentType && contentType.indexOf('.') !== -1) {
				return contentType.split('.')[1];
			} else {
				return null;
			}
		}

		getPropertyList() {
			const propertyList = [];

			if (this._isTopLevelPage()) {
				propertyList.push(...this._getTopLevelLayoutPropertiesList());
				// All layouts have the background color property
				propertyList.push(this._getBackgroundColorProperty());
			}

			// Default the setPropertyValue and getPropertyValue for any property that doesn't define those methods
			propertyList.forEach((property) => {
				if (!property.setPropertyValue) {
					property.setPropertyValue = (value) => this._setPropertyValueInModel(property.id, value);
				}

				if (!property.getPropertyValue) {
					property.getPropertyValue = () => this._getPropertyValueFromModel(property.id);
				}
			});

			return propertyList;
		}

		getPropertyLayoutList() {
			let propertiesLayoutSpec = [];

			if (this._isTopLevelPage()) {
				propertiesLayoutSpec = this._getTopLevelPropertiesLayoutSpec();
			}

			return propertiesLayoutSpec;
		}

		reset() { }

		_getTopLevelPropertiesLayoutSpec() {
			const topLevelPropertiesLayoutSpec = [{
				id: 'general',
				type: 'Group', // deprecated in Carbon design
				label: StringResources.get('tabName_general')
			}, {
				id: 'canvas',
				type: 'Section',
				label: StringResources.get('sectionName_canvas'),
				position: 1 // becomes position = 2 in Carbon design
			}, {
				id: 'pageSize',
				type: 'Section',
				collapsible: false,
				label: StringResources.get('propPageSizeLabel'),
				position: 2 // no longer a section in Carbon design
			}, {
				id: 'colorsAndTheme',
				type: 'Section',
				label: StringResources.get('sectionName_colorsAndThemes'),
				position: 3 // becomes position = 1 in Carbon design
			}, {
				id: 'grid',
				type: 'Section',
				collapsible: false,
				label: StringResources.get('gridLabel'),
				position: 2 // becomes position = 3 in Carbon design
			}, {
				id: 'pageSizeSplit', // position = 4 in Carbon design
				type: 'Split',
				items: [{
					id: 'left',
					align: 'left',
					items: []
				}, {
					id: 'right',
					align: 'right',
					items: []
				}]
			}];
			if (this.isPropertiesV2Enabled) {
				// Re-order sections to comply to Carbon design.
				// When FF 'PropertiesV2' is enabled, we should modify directly the layout.
				topLevelPropertiesLayoutSpec[3].position = 1; //colorsAndTheme
				topLevelPropertiesLayoutSpec[1].position = 2; // canvas
				topLevelPropertiesLayoutSpec[4].position = 3; // grid
				topLevelPropertiesLayoutSpec[5].position = 4; // pageSizeSplit

				// Remove the group 'general' and section 'pageSize'
				topLevelPropertiesLayoutSpec.splice(0, 1);
				topLevelPropertiesLayoutSpec.splice(1, 1);
			}

			return topLevelPropertiesLayoutSpec;
		}

		_getTopLevelLayoutPropertiesList() {
			const _this = this;
			const model = this.getLayoutModel();
			let sectionCanvas, sectionPageSize;
			if (this.isPropertiesV2Enabled) {
				sectionCanvas = 'canvas';
				sectionPageSize = sectionCanvas; // pageSize is no longer a section in Carbon design
			} else {
				sectionCanvas = 'general.canvas';
				sectionPageSize = sectionCanvas + '.pageSize';
			}

			return [
				{
					id: 'layoutPositioning',
					getPropertyValue: () => {
						return model.getValueFromSelfOrParent('layoutPositioning');
					},
					setPropertyValue: this._setLayoutPositioning.bind(this),
					validatePropertyValue: (value) => {
						return {
							isValid: ['relative', 'absolute'].includes(value),
							message: 'layoutPositioning must be either relative or absolute.'
						};
					},
					editor: {
						sectionId: sectionCanvas,
						position: 1,
						uiControl: {
							type: 'DropDown',
							label: StringResources.get('propLayoutPositioning'),
							getOptions: this._getLayoutPositioningSpecOptions.bind(this),
							coachMark: {
								render: (options) => {
									Utils.addCoachmark({
										id: 'com.ibm.bi.dashboard.dashboardCoreProperties.layoutPositioning',
										title: StringResources.get('propLayoutPositioningCoachmarkTitle'),
										contents: StringResources.get('propLayoutPositioningCoachmarkContent'),
										placement: 'bottom',
										glassContext: options.glassContext,
										$el: options.$el
									});
								}
							},
							onChange: function(propertyName, propertyValue) {
								_this._onChangeLayoutPosition(this, propertyValue);
							}
						}
					}
				},
				{
					id: 'pageSizePreset',
					getPropertyValue: this._getPageSizePreset.bind(this),
					setPropertyValue: this._setPageSizePreset.bind(this),
					editor: {
						sectionId: sectionPageSize,
						position: 1,
						uiControl: {
							type: 'DropDown',
							label: StringResources.get('propPageSizePreset'),
							ariaLabel: StringResources.get('propPageSizePresetAriaLabel'),
							options: PRESET_PAGE_SIZES,
							onChange: this._onChangePresetPageSize.bind(this)
						}
					}
				},
				{
					id: 'fitPage',
					getPropertyValue: () => {
						let fitPage = false;
						if (model.getValueFromSelfOrParent('layoutPositioning') !== 'absolute') {
							fitPage = model.getValueFromSelfOrParent('fitPage') || false;
						}
						return fitPage;
					},
					setPropertyValue: (value) => {
						model.set({ 'fitPage': value });
					},
					editor: {
						sectionId: sectionPageSize,
						position: 2,
						uiControl: {
							type: 'ToggleButton',
							label: StringResources.get('fitPageText')
						}
					}
				},
				{
					id: 'pageSizeWidth',
					getPropertyValue: () => {
						const pageSize = this._getPropertyValueFromModel('pageSize');
						return pageSize && pageSize.width;
					},
					setPropertyValue: (value) => {
						this._setPageSizeDirection(value, 'pageSizeWidth', true);
					},
					editor: {
						sectionId: sectionPageSize + '.pageSizeSplit.left',
						uiControl: {
							'type': 'InputLabel',
							'label': StringResources.get('propPageSizeWidth'),
							'ariaLabel': StringResources.get('propPageSizeWidthAriaLabel'),
							'multiline': true,
							'handleReturnKey': true,
							'onChange': function(propertyName, propertyValue) {
								_this._onChangePageSize(propertyName, propertyValue, this);
							}
						}
					}
				},
				{
					id: 'pageSizeHeight',
					getPropertyValue: () => {
						const pageSize = this._getPropertyValueFromModel('pageSize');
						return pageSize && pageSize.height;
					},
					setPropertyValue: (value) => {
						this._setPageSizeDirection(value, 'pageSizeHeight', true);
					},
					editor: {
						sectionId: sectionPageSize + '.pageSizeSplit.right',
						uiControl: {
							'type': 'InputLabel',
							'label': StringResources.get('propPageSizeHeight'),
							'ariaLabel': StringResources.get('propPageSizeHeightAriaLabel'),
							'multiline': true,
							'handleReturnKey': true,
							'onChange': function(propertyName, propertyValue) {
								_this._onChangePageSize(propertyName, propertyValue, this);
							}

						}
					}
				},
				{
					id: 'showGrid',
					editor: {
						sectionId: sectionCanvas + '.grid',
						position: 1,
						uiControl: {
							type: 'ToggleButton',
							label: StringResources.get('gridText')
						}
					}
				},
				{
					id: 'snapGrid',
					editor: {
						sectionId: sectionCanvas + '.grid',
						position: 2,
						uiControl: {
							type: 'ToggleButton',
							label: StringResources.get('snapText')
						}
					}
				},
				{
					id: 'snapObjects',
					editor: {
						sectionId: sectionCanvas + '.grid',
						position: 3,
						uiControl: {
							type: 'ToggleButton',
							label: StringResources.get('snapObjectsText')
						}
					}
				}
			];
		}

		_getBackgroundColorProperty() {
			let sectionId;
			if (this.isPropertiesV2Enabled) {
				sectionId = 'colorsAndTheme';
			} else {
				sectionId = 'general.colorsAndTheme';
			}
			return {
				id: 'fillColor',
				getPropertyValue: () => this._getPropertyValueFromModel('fillColor') || 'transparent',
				setPropertyValue: this._setFillColor.bind(this),
				supportsUndoRedo: true,
				editor: {
					sectionId,
					position: 2,
					uiControl: {
						type: 'ColorPicker',
						label: StringResources.get('propDashboardBackgroundColor'),
						ariaLabel: StringResources.get('propDashboardBackgroundColor'),
						paletteType: 'DashboardColorSet',
						open: false,
						showHexValue: true,
						addButton: true
					}
				}
			};
		}

		_setFillColor(propertyValue) {
			const payload = {
				fillColor: this._features['Colors'].getColorClassName(propertyValue)
			};
			this._features['Colors'].prepareForColorModelChange(payload, 'fillColor');

			const model = this.getLayoutModel();
			model.set(payload, {
				payloadData: { skipUndoRedo: true }
			});
		}

		_getPageSizePreset() {
			const model = this.getLayoutModel();
			let { width, height } = model.get('pageSize');
			let preset = 'custom';
			const checkWidth = parseInt(width, 10),
				checkHeight = parseInt(height, 10);

			for (let value in PAGESIZES) {
				if (PAGESIZES.hasOwnProperty(value)) {
					const currentPreset = PAGESIZES[value];
					if (currentPreset.width === checkWidth && currentPreset.height === checkHeight) {
						preset = value;
						break;
					}
				}
			}

			return preset;
		}

		_setPageSizePreset(propertyValue) {
			const presetValues = PAGESIZES[propertyValue];
			if (presetValues) {
				if (this.getCanvas().getPropertyValue('layoutPositioning') !== 'absolute') {
					this.getCanvas().setPropertyValue('fitPage', propertyValue === '16:9' || propertyValue === '4:3' ? true : false);
				}
				const currentPageSizeWidth = this.getCanvas().getPropertyValue('pageSizeWidth');
				const currentPageSizeHeight = this.getCanvas().getPropertyValue('pageSizeHeight');
				if (!currentPageSizeWidth || currentPageSizeWidth !== presetValues.width || !currentPageSizeHeight || currentPageSizeHeight !== presetValues.height) {
					this._setPageSize({
						width: presetValues.width,
						height: presetValues.height
					}, true);
				}
			}
		}

		_onChangePresetPageSize(propertyName, propertyValue) {
			const presetValues = PAGESIZES[propertyValue];
			if (presetValues) {
				const model = this.getLayoutModel();
				if (model.get('layoutPositioning') !== 'absolute') {
					// @todo: can we explicity set the property without triggering an event?
					this.dashboard.triggerDashboardEvent('properties:setValue', {
						propertyName: 'fitPage',
						value: (propertyValue === '16:9' || propertyValue === '4:3') ? true : false
					});
				}

				this.getCanvas().setPropertyValue(propertyName, propertyValue);
			}
		}

		_getPropertyValueFromModel(propName) {
			const model = this.getLayoutModel();
			// In case of livewidget previews, we have a content but it is not attached to the layout, which causes the model to be null.
			return model && model.get(propName);
		}

		_setPropertyValueInModel(propName, propValue, options) {
			const payload = {};
			const model = this.getLayoutModel();
			payload[propName] = propValue;
			model.set(payload, options);
		}

		_isTopLevelPage() {
			return !this.content.getContainer();
		}

		_onChangeLayoutPosition(propertyControl, propertyValue) {
			const transactionToken = this._features['Transaction'].startTransaction();
			if (!transactionToken.undoRedoTransactionId) {
				transactionToken.undoRedoTransactionId = transactionToken.transactionId;
			}
			const oldLayoutPositioning = this.getCanvas().getPropertyValue('layoutPositioning');
			if (oldLayoutPositioning) {
				if (propertyValue === 'absolute') {
					this.dashboard.triggerDashboardEvent('properties:updateEnabled', { propertyName: 'fitPage', enabled: false });
					this.dashboard.triggerDashboardEvent('properties:setValue', { propertyName: 'fitPage', value: false });
				} else {
					this.dashboard.triggerDashboardEvent('properties:updateEnabled', { propertyName: 'fitPage', enabled: true });
					const preset = this._getPageSizePreset();
					if (preset === '16:9' || preset === '4:3') {
						this.dashboard.triggerDashboardEvent('properties:setValue', { propertyName: 'fitPage', value: true });
					} else {
						this.dashboard.triggerDashboardEvent('properties:setValue', { propertyName: 'fitPage', value: false });
					}
				}
				this.getCanvas().setPropertyValue('layoutPositioning', propertyValue, transactionToken);
				this._features['Transaction'].endTransaction(transactionToken);
			} else {
				// Confirm layout upgrade with user
				const onOK = () => {
					this.getCanvas().setPropertyValue('layoutPositioning', propertyValue, transactionToken);
					this._features['Transaction'].endTransaction(transactionToken);
					// Update property control - which should remove "undefined" as an option
					// Ideally we should only be updating the one control of interest.
					// But, this doesn't currently work right for items in a section block.
					// let event = {
					// 	'propertySpec': this._getLayoutPositioningSpec(),
					// 	'removeProperty': false
					// };
					// this.dashboard.triggerDashboardEvent('properties:refreshProperty', event);
					this.dashboard.triggerDashboardEvent('properties:refreshPane');
				};
				const onCancel = () => {
					// Reset back to undefined state
					propertyControl._lastChangedValue = 'undefined';  // Bug in glass, setValue doesn't reset _lastChangedValue.
					propertyControl.setValue('undefined');
				};
				const oConfirmationDialog = new ConfirmationDialog({
					title: StringResources.get('confirmLayoutStyleUpgradeTitle'),
					message: StringResources.get('confirmLayoutStyleUpgrade'),
					confirmCallback: onOK,
					cancelCallback: onCancel
				});
				oConfirmationDialog.render();
			}
		}

		_setLayoutPositioning(propertyValue, transactionToken) {
			const data = this._features['Transaction'].startTransaction(transactionToken);
			if (!data.undoRedoTransactionId) {
				data.undoRedoTransactionId = data.transactionId;
			}
			const model = this.getLayoutModel();
			model.set(
				{ layoutPositioning: propertyValue },
				{
					payloadData: data,
					sender: this.senderId
				}
			);
			if (propertyValue === 'absolute') {
				model.set(
					{ 'fitPage': false },
					{
						payloadData: data,
						sender: this.senderId
					}
				);
			} else {
				const preset = this._getPageSizePreset();
				if (preset === '16:9' || preset === '4:3') {
					model.set(
						{ 'fitPage': true },
						{
							payloadData: data,
							sender: this.senderId
						}
					);
				} else {
					model.set(
						{ 'fitPage': false },
						{
							payloadData: data,
							sender: this.senderId
						}
					);
				}
			}

			this._features['Transaction'].endTransaction(data);
			this.dashboard.triggerDashboardEvent('layout:resize');
		}

		_getLayoutPositioningSpecOptions() {
			const currentValue = this.getCanvas().getPropertyValue('layoutPositioning');
			let layoutPositioningOptions = [];
			if (!currentValue) {
				layoutPositioningOptions.push({
					label: '',
					value: 'undefined'
				});
			}
			layoutPositioningOptions.push(
				{
					label: StringResources.get('propLayoutRelative'),
					value: 'relative'
				},
				{
					label: StringResources.get('propLayoutAbsolute'),
					value: 'absolute'
				}
			);
			return layoutPositioningOptions;
		}

		_onChangePageSize(propertyName, propertyValue, propertyControl) {
			if (propertyControl.propertiesManagerChange) {
				propertyControl.onChangeValueHold = propertyValue;
			} else {
				this.getCanvas().setPropertyValue(propertyName, propertyValue);

				// It's possible the value got tweaked because of the bounding rect size.
				const currentValue = parseInt(this.getCanvas().getPropertyValue(propertyName), 10);
				const propertyNameList = ['pageSizeSplit', propertyName];
				if (isNaN(currentValue)) {
					this.dashboard.triggerDashboardEvent('properties:setValue', { propertyName: propertyNameList, value: propertyControl.onChangeValueHold });
				} else if (propertyValue !== propertyControl.onChangeValueHold) {
					propertyControl.onChangeValueHold = PropertyListUtil.getPropertyDisplayString(currentValue, StringResources.get('pixelUnit'), true);
					this.dashboard.triggerDashboardEvent('properties:setValue', { propertyName: propertyNameList, value: propertyControl.onChangeValueHold });
				}
			}
		}

		_showPageSizeChangeToast() {
			this.dashboard.showToast(StringResources.get('propPageSizeAdjustedMessage'), {
				type: 'info'
			});
		}

		_setPageSizeDirection(propValue, propName, showToast = false) {
			let numericValue = parseInt(propValue, 10);
			if (!isNaN(numericValue)) {
				const model = this.getLayoutModel();
				const direction = propName === 'pageSizeWidth' ? 'width' : 'height';
				const otherDirection = propName === 'pageSizeWidth' ? 'height' : 'width';
				const currentPageSize = model.get('pageSize') || {};

				if (currentPageSize[direction] === numericValue) {
					return;
				}
				const newPageSize = {};
				newPageSize[direction] = numericValue;
				newPageSize[otherDirection] = currentPageSize[otherDirection];

				this._setPageSize(newPageSize, showToast);

				// Not modelled as a seperate property, but our API exposes it.  Throw an event in case anybody is listening to it
				const eventName = direction === 'width' ? 'change:pageSizeWidth' : 'change:pageSizeHeight';
				model.trigger(eventName, {
					value: newPageSize[direction],
					model: model,
					eventName: eventName
				});
			}
		}

		_setPageSize(newSize, showToast = false) {
			const model = this.getLayoutModel();
			let sizeChanged = false;
			const minimumSize = LayoutHelper.getBoundingPageSize(model, MINIMUM_PAGESIZE);
			if (minimumSize) {
				if (isNaN(newSize.width) || minimumSize.width > newSize.width) {
					newSize.width = minimumSize.width;
					sizeChanged = true;
				}
				if (isNaN(newSize.height) || minimumSize.height > newSize.height) {
					newSize.height = minimumSize.height;
					sizeChanged = true;
				}

				if (sizeChanged && showToast) {
					this._showPageSizeChangeToast();
				}
			}

			model.set({
				'pageSize': newSize
			});

			this.dashboard.triggerDashboardEvent('layout:resize');

			return newSize;
		}
	}

	return TopLevelProperties;
});
